import React from 'react'
import { ToleranceGroupSummaryDTO, ToleranceGroupDTO } from '../../../domain/DTO'
import ToleranceGroupInput from './ToleranceGroupInput'
import { DeleteIcon } from '../../../icons'
import { ToleranceGroupDbInput } from './ToleranceGroupDbInput'
import { authenticatedPost } from '../../../lib/authenticatedGet'
import { API_HOST } from '../../../lib'
import { logError } from '../../../lib/logError'

type ToleranceFieldConfig = {
  label: string
  placeholder: string
  field: keyof ToleranceGroupDTO
}

type Props = {
  index: number
  commonId: string
  toleranceGroupSummaryDTO: ToleranceGroupSummaryDTO
  toleranceFields: ToleranceFieldConfig[]
  onFieldChange: (field: keyof ToleranceGroupDTO, value: number | null, toleranceGroupId: number) => void
  onDelete: (index: number) => void
}

export const ToleranceGroupRow = ({
  index,
  commonId,
  toleranceGroupSummaryDTO,
  toleranceFields,
  onFieldChange,
  onDelete,
}: Props) => {
  const isToleranceGroupFromCpgFile = toleranceGroupSummaryDTO.toleranceGroupsFromCpg?.some(
    (group) => group.toleranceGroupId === toleranceGroupSummaryDTO.toleranceGroupId,
  )

  const isToleranceGroupOnlyInDb =
    toleranceGroupSummaryDTO.toleranceGroupsFromDb?.some(
      (group) => group.toleranceGroupId === toleranceGroupSummaryDTO.toleranceGroupId,
    ) && !isToleranceGroupFromCpgFile

  const handleExpire = async () => {
    try {
      await authenticatedPost<void>(
        `${API_HOST}/bff/common/${commonId}/tolerance-group/${toleranceGroupSummaryDTO.toleranceGroupId}/expire`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(toleranceGroupSummaryDTO.toleranceGroupId),
        },
        120000,
      )
      onDelete(index)
    } catch (err) {
      logError(new Error('Failed to remove tolerance group', { cause: err as Error }))
    }
  }

  return (
    <div key={index} className="flex items-end gap-1">
      {toleranceFields.map((fieldConfig) => {
        const matchingDbGroup = toleranceGroupSummaryDTO.toleranceGroupsFromDb?.find(
          (group) => group.toleranceGroupId === toleranceGroupSummaryDTO.toleranceGroupId,
        )
        const fieldValueDb = (matchingDbGroup as unknown as ToleranceGroupDTO)?.[fieldConfig.field]

        const matchingCpgGroup = toleranceGroupSummaryDTO.toleranceGroupsFromCpg?.find(
          (group) => group.toleranceGroupId === toleranceGroupSummaryDTO.toleranceGroupId,
        )
        const fieldValueCpg = (matchingCpgGroup as unknown as ToleranceGroupDTO)?.[fieldConfig.field]

        if (typeof fieldValueDb !== 'number' && typeof fieldValueCpg !== 'number') return null

        return (
          <div className="inline-flex flex-col w-10" key={fieldConfig.field}>
            {fieldConfig.field === 'toleranceGroupId' && isToleranceGroupFromCpgFile && (
              <span
                className="flex items-center px-1 bg-blue border rounded-[10px] w-3 leading-10"
                title="Existing value in cpg file"
              >
                {typeof fieldValueCpg === 'number' ? fieldValueCpg : null}
              </span>
            )}
            {(fieldConfig.field !== 'toleranceGroupId' || !isToleranceGroupFromCpgFile) &&
              !isToleranceGroupOnlyInDb && (
                <ToleranceGroupInput
                  dbValue={typeof fieldValueDb === 'number' ? fieldValueDb : null}
                  cpgValue={typeof fieldValueCpg === 'number' ? fieldValueCpg : null}
                  field={fieldConfig.field}
                  onChange={(value) =>
                    onFieldChange(fieldConfig.field, value, toleranceGroupSummaryDTO.toleranceGroupId)
                  }
                />
              )}

            {isToleranceGroupOnlyInDb && (
              <ToleranceGroupDbInput
                key={`${index}-${fieldConfig.field}`}
                dbValue={typeof fieldValueDb === 'number' ? fieldValueDb : null}
                onChange={(value) => onFieldChange(fieldConfig.field, value, toleranceGroupSummaryDTO.toleranceGroupId)}
                field={fieldConfig.field}
              />
            )}
          </div>
        )
      })}

      {!isToleranceGroupFromCpgFile && (
        <button type="button" className="pb-sm" title="Remove tolerance group row" onClick={handleExpire}>
          <DeleteIcon />
        </button>
      )}
    </div>
  )
}
